import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { isSuccess } from '../../../lib/packingListRequest';
import { fromDeleteImpact, isConfirmedDoc } from '../../../lib/packingListWire';

const IMPACT_ROWS = [
    { key: 'items', label: 'Lines' },
    { key: 'allocations', label: 'Container allocations' },
    { key: 'shipperData', label: 'Customer data' },
];

/**
 * 확정본을 지우면 함께 사라지는 건수를 연 순간 서버에서 읽는다. 초안이나 아직 서버에 없는 문서는 읽지 않는다.
 * status는 idle(읽을 것 없음)·loading·ready·unavailable·error다. 다시 열면 새로 읽는다 — 그 사이 배분이 바뀌었을 수 있다.
 * unavailable은 서버에 건수 조회가 아직 없다는 뜻(OPERATION_UNKNOWN)이라 건수 없이 지우게 둔다. 그 밖의 실패는 error다.
 * 창을 닫거나 다른 문서로 다시 연 뒤에 도착한 이전 응답은 버린다 — 다른 문서의 건수로 Delete가 열리면 안 된다.
 */
function useDeleteImpact(commands, doc) {
    const [state, setState] = useState({ status: 'idle', impact: null });
    const id = isConfirmedDoc(doc) ? doc.id : null;
    /** 마지막으로 보낸 조회의 번호. 응답이 오면 이 번호와 같을 때만 반영한다. */
    const requestSeq = useRef(0);

    const load = useCallback(async () => {
        requestSeq.current += 1;
        if (id == null || !commands) return;
        const seq = requestSeq.current;
        setState({ status: 'loading', impact: null });
        const result = await commands.readDeleteImpact({ id });
        if (seq !== requestSeq.current) return;
        if (isSuccess(result)) setState({ status: 'ready', impact: fromDeleteImpact(result.data) });
        else setState({ status: result.code === 'OPERATION_UNKNOWN' ? 'unavailable' : 'error', impact: null });
    }, [commands, id]);

    useEffect(() => {
        if (id != null) {
            load();
            return;
        }
        requestSeq.current += 1;
        setState({ status: 'idle', impact: null });
    }, [id, load]);

    return { ...state, reload: load };
}

/**
 * 문서를 지우기 전 경고. 확정본이면 함께 지워지는 라인·배분·화주 데이터 건수를 서버에서 읽어 보여주고, 건수를 읽은 뒤에만 Delete를 누를 수 있다.
 * 건수를 못 읽으면 Retry로 다시 읽는다. 서버에 건수 조회가 아직 없으면 그렇다고 알리고 건수 없이 지울 수 있다. 초안은 건수 없이 저장한 편집이 사라진다고만 알린다.
 * doc이 null이면 닫혀 있다. 지우는 요청이 진행 중(busy)이면 닫거나 다시 누를 수 없다.
 */
export default function DeleteDocumentDialog({ doc, busy, commands, onCancel, onConfirm }) {
    const confirmed = isConfirmedDoc(doc);
    const name = doc?.invoice_no || (confirmed ? 'This packing list' : 'This draft');
    const impact = useDeleteImpact(commands, doc);
    const impactPending = confirmed && doc?.id != null && !['ready', 'unavailable'].includes(impact.status);
    return (
        <Dialog open={Boolean(doc)} onClose={() => !busy && onCancel()} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontSize: 14, color: '#003a52' }}>{confirmed ? 'Delete this confirmed packing list?' : 'Delete this draft?'}</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                <Typography sx={{ fontSize: 12 }}>
                    {confirmed ? (
                        <>
                            <b>{name}</b> will be removed with its items and container allocations. Shippers will no longer see it. This cannot be undone.
                        </>
                    ) : (
                        <>
                            <b>{name}</b> will be removed and its saved edits are lost. This cannot be undone.
                        </>
                    )}
                </Typography>
                {confirmed && impact.status === 'loading' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={14} />
                        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Counting what will be deleted…</Typography>
                    </Box>
                )}
                {confirmed && impact.status === 'ready' && (
                    <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 0.5, columnGap: 2, px: 1.5, py: 1, bgcolor: '#fafafa', border: '1px solid #e6ebee', borderRadius: '6px' }}>
                        {IMPACT_ROWS.map((row) => (
                            <Box key={row.key} sx={{ display: 'contents' }}>
                                <Typography component="dt" sx={{ fontSize: 12, color: '#003a52' }}>{row.label}</Typography>
                                <Typography component="dd" data-testid={`delete-impact-${row.key}`} sx={{ m: 0, fontSize: 13, fontWeight: 600, color: impact.impact[row.key] > 0 ? 'error.main' : '#003a52', textAlign: 'right' }}>
                                    {impact.impact[row.key].toLocaleString()}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                )}
                {confirmed && impact.status === 'unavailable' && <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>The server cannot count the affected lines and allocations yet.</Typography>}
                {confirmed && impact.status === 'error' && (
                    <Alert severity="error" sx={{ py: 0, fontSize: 12, alignItems: 'center' }} action={<Button size="small" color="inherit" onClick={impact.reload} sx={{ fontSize: 11 }}>Retry</Button>}>
                        Could not count what will be deleted.
                    </Alert>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button size="small" onClick={onCancel} disabled={busy} sx={{ fontSize: 12 }}>Cancel</Button>
                <Button size="small" variant="contained" color="error" onClick={onConfirm} disabled={busy || impactPending} sx={{ fontSize: 12 }}>Delete</Button>
            </DialogActions>
        </Dialog>
    );
}
